import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  LeaveBalance,
  LeaveBalanceUpsertPayload,
  LeaveRequest,
  LeaveRequestPayload,
} from '../types/leaveRequest';
import { DEFAULT_PAGE_SIZE, PageResponse } from '../types/page';

export async function fetchLeaveRequests(params?: {
  page?: number;
  size?: number;
}): Promise<PageResponse<LeaveRequest>> {
  const { data } = await apiClient.get<PageResponse<LeaveRequest>>('/leave-requests', {
    params: {
      page: params?.page ?? 0,
      size: params?.size ?? DEFAULT_PAGE_SIZE,
    },
  });
  return data;
}

export async function fetchLeaveRequest(leaveRequestId: number): Promise<LeaveRequest> {
  const { data } = await apiClient.get<LeaveRequest>(`/leave-requests/${leaveRequestId}`);
  return data;
}

export async function createLeaveRequest(payload: LeaveRequestPayload): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>('/leave-requests', payload);
  return data;
}

export async function updateLeaveRequest(
  leaveRequestId: number,
  payload: LeaveRequestPayload
): Promise<LeaveRequest> {
  const { data } = await apiClient.patch<LeaveRequest>(`/leave-requests/${leaveRequestId}`, payload);
  return data;
}

export async function deleteLeaveRequest(leaveRequestId: number): Promise<void> {
  await apiClient.delete(`/leave-requests/${leaveRequestId}`);
}

export async function approveLeaveRequest(leaveRequestId: number): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(`/leave-requests/${leaveRequestId}/approve`);
  return data;
}

export async function revokeLeaveRequest(leaveRequestId: number): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(`/leave-requests/${leaveRequestId}/revoke`);
  return data;
}

export interface LeaveCalendarItem {
  leaveRequestId: number;
  leaveType: string;
  startDate: string;
  endDate: string;
  days?: number | null;
  halfDayPeriod?: string | null;
  requesterUserId?: number | null;
  requesterName?: string | null;
}

export async function fetchLeaveCalendar(
  from: string,
  to: string
): Promise<LeaveCalendarItem[]> {
  const { data } = await apiClient.get<LeaveCalendarItem[]>('/leave-requests/calendar', {
    params: { from, to },
  });
  return data;
}

export async function fetchMyLeaveBalance(year?: number): Promise<LeaveBalance> {
  const { data } = await apiClient.get<LeaveBalance>('/leave-balances/me', {
    params: year != null ? { year } : undefined,
  });
  return data;
}

export async function fetchUserLeaveBalance(
  userId: number,
  year?: number
): Promise<LeaveBalance> {
  const { data } = await apiClient.get<LeaveBalance>('/leave-balances', {
    params: {
      userId,
      ...(year != null ? { year } : {}),
    },
  });
  return data;
}

export async function upsertLeaveBalance(
  payload: LeaveBalanceUpsertPayload
): Promise<LeaveBalance> {
  const { data } = await apiClient.put<LeaveBalance>('/leave-balances', payload);
  return data;
}

export async function autoGrantLeaveBalance(
  userId: number,
  year?: number
): Promise<LeaveBalance> {
  const { data } = await apiClient.post<LeaveBalance>('/leave-balances/auto-grant', null, {
    params: {
      userId,
      ...(year != null ? { year } : {}),
    },
  });
  return data;
}


export async function downloadLeaveRequestPdf(
  leaveRequestId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/leave-requests/${leaveRequestId}/pdf`,
    fileName || `연차신청서_${leaveRequestId}.pdf`
  );
}
